import { useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { Dialog } from "@/components/ui/Dialog";
import { cn } from "@/lib/utils";
import { combinePageImageUrl } from "./api";
import {
  acceptPosition,
  approvedPageIdOf,
  canUseApproved,
  collectMoves,
  effectivePageIdOf,
  groupRows,
  moveRow,
  notices,
  pageIdOf,
  positionLabel,
  rowLabel,
  summarize,
  toPageRefs,
  toRows,
  toggleIncluded,
  toggleUseApproved,
  type CombineListItem,
} from "./plan";
import type {
  CombineMove,
  CombinePageRef,
  CombinePlan,
  CombineRow,
} from "./types";

// The combined plan set, before it is built.
//
// What this screen is for, in one line: a superseding sheet's position is known
// and a new sheet's is not. So it shows the whole document in final order, says
// on every inserted row what its position was inferred from, and lets a person
// drag it. Nothing here blocks the build — a checklist that must be cleared
// before every filing is a checklist people learn to click through.
//
// The order on screen IS the page list that gets sent (see plan.ts): one array,
// one derivation. A list that reads correctly and builds something else would be
// invisible — the PDF just comes out wrong.
export function CombineStep({
  plan,
  rows,
  onRows,
  combineId,
  busy,
  built,
  onBuild,
  onBack,
}: {
  plan: CombinePlan;
  /** The order under review. CONTROLLED, held by the page: this screen is a side
   *  trip off review and download, and unmounting it must not throw away a
   *  reordering somebody just did. */
  rows: CombineRow[];
  onRows: (next: CombineRow[]) => void;
  combineId: string | null;
  busy: boolean;
  /** The file, once built. Stays on screen while the list does, because the
   *  usual next move after looking at a combined set is to change something and
   *  build it again. */
  built: { url: string; name: string } | null;
  onBuild: (pages: CombinePageRef[], moves: CombineMove[]) => void;
  onBack: () => void;
}) {
  // The proposal, kept for comparison: what the operator changed about it is the
  // only honest signal that the placement rule is wrong.
  const proposed = useMemo(() => toRows(plan), [plan]);
  const unbuildable = useMemo(() => notices(plan), [plan]);

  // Once it is built, the file IS the screen — the same terminal state the AI1
  // download gets. "Adjust the order" comes back to the list, and the built file
  // stays offered there until the order actually changes under it (the page
  // clears it on any edit, so a stale download is never on offer).
  const [adjusting, setAdjusting] = useState(false);

  const [onlyChanges, setOnlyChanges] = useState(true);
  const [expandedRuns, setExpandedRuns] = useState<ReadonlySet<string>>(new Set());
  const [preview, setPreview] = useState<{ row: CombineRow; side: "new" | "old" } | null>(null);

  // Both are state, not refs: the row being dragged and the gap it would land in
  // are things the operator has to SEE, and a ref changes nothing on screen.
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dropAt, setDropAt] = useState<number | null>(null);

  const summary = useMemo(() => summarize(rows, unbuildable.length), [rows, unbuildable]);
  const items = useMemo(
    () => groupRows(rows, onlyChanges, expandedRuns),
    [rows, onlyChanges, expandedRuns],
  );

  const move = (from: number, to: number) => onRows(moveRow(rows, from, to));

  const expandRun = (key: string) =>
    setExpandedRuns((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });

  const build = () => onBuild(toPageRefs(rows), collectMoves(proposed, rows));

  if (built && !adjusting) {
    return (
      <div className="animate-reveal max-w-5xl">
        <Card>
          <CardBody className="space-y-6 py-12 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-sage-soft text-sage-ink">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M5 12l4 4L19 7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-[1.5rem] font-medium text-ink">Combined plan set ready</h2>
              <p className="mt-1 text-[0.95rem] text-ink-soft">{built.name}</p>
              <p className="mt-1 text-[0.88rem] text-ink-muted">
                {summary.pages} pages · {summary.replaced} replaced · {summary.added} added
                {summary.removed > 0 ? ` · ${summary.removed} removed` : ""}
                {summary.keptApproved > 0 ? ` · ${summary.keptApproved} kept approved` : ""}
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <a href={built.url} download={built.name}>
                <Button intent="primary">Download combined PDF</Button>
              </a>
              <Button intent="secondary" onClick={() => setAdjusting(true)}>
                Adjust the order
              </Button>
              <Button intent="ghost" onClick={onBack}>
                Done
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="animate-reveal max-w-5xl">
      <div className="mb-8">
        <div className="label mb-2">Combine</div>
        <h1 className="text-[clamp(2rem,4vw,2.85rem)] font-medium tracking-tight text-ink">
          Combine the plan sets
        </h1>
        <p className="mt-3 max-w-2xl text-[1rem] leading-relaxed text-ink-soft">
          The approved set with the superseding sheets swapped in, omitted sheets
          taken out, and the new sheets inserted. Superseding sheets sit exactly
          where the sheets they replace sat; the new ones are placed by sheet
          number — check those, and drag anything that belongs somewhere else.
        </p>
      </div>

      {built && (
        <Card className="mb-4 border-sage/40 bg-sage-soft/50">
          <CardBody className="flex flex-wrap items-center justify-between gap-4 py-3">
            <div className="min-w-0 text-[0.9rem] text-ink">
              Built from this order —{" "}
              <span className="text-ink-soft">{built.name}</span>
            </div>
            <a href={built.url} download={built.name} className="shrink-0">
              <Button intent="secondary" size="sm">
                Download
              </Button>
            </a>
          </CardBody>
        </Card>
      )}

      {plan.approvedSheetsUnreadable && (
        <Notice tone="warn" title="No sheet numbers could be read in the approved set">
          Every approved page is still carried over in its own order, but nothing
          could be matched — so the revised sheets were added rather than swapped
          in, and the old versions are still in the set. Place them by hand, or go
          back and check you uploaded the right file.
        </Notice>
      )}
      {!plan.approvedSheetsUnreadable && plan.approvedSheetsReadVisually && (
        <Notice tone="sky" title="The approved set's sheet numbers were read visually">
          It has no usable text layer (a scan), so each title block was read as an
          image. Worth a look at the sheet numbers below before building.
        </Notice>
      )}

      {/* What is about to be built, counted from the rows on screen. */}
      <Card className="mb-4">
        <CardBody className="flex flex-wrap items-center gap-x-6 gap-y-3 py-4">
          <div className="mr-2">
            <div className="font-display text-[1.6rem] leading-none text-ink">
              {summary.pages}
            </div>
            <div className="text-[0.78rem] text-ink-muted">pages</div>
          </div>
          {/* Zero of something is not information — a row of noughts is what makes
              the two numbers that matter hard to find. */}
          <Chip tone="neutral" count={summary.carried} label="carried over" />
          <Chip tone="sky" count={summary.replaced} label="replaced" />
          <Chip tone="sage" count={summary.added} label="added" />
          <Chip tone="warn" count={summary.removed} label="removed" />
          <Chip tone="warn" count={summary.keptApproved} label="kept approved" />
          <Chip tone="butter" count={summary.check} label="to check" />
          <Chip tone="warn" count={summary.warnings} label="to look at" />
        </CardBody>
      </Card>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-md border border-rule bg-surface p-0.5">
          {(
            [
              ["changes", "Changes only"],
              ["all", "Every page"],
            ] as const
          ).map(([key, label]) => {
            const active = (key === "changes") === onlyChanges;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setOnlyChanges(key === "changes")}
                className={cn(
                  "rounded-[5px] px-3 py-1.5 text-[0.82rem] font-medium transition-colors",
                  active ? "bg-primary text-white" : "text-ink-soft hover:text-ink",
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
        <div className="text-[0.84rem] text-ink-muted">
          {summary.check > 0
            ? `${summary.check} position${summary.check === 1 ? "" : "s"} we worked out ourselves`
            : "Every position is either the approved set's or yours"}
        </div>
      </div>

      <Card>
        <CardBody className="p-0">
          <ol className="divide-y divide-rule">
            {items.map((item) =>
              item.kind === "run" ? (
                <RunLine
                  key={item.key}
                  item={item}
                  dropAt={dropAt}
                  onExpand={() => expandRun(item.key)}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDropAt(item.firstIndex + item.count);
                  }}
                  onDrop={() => {
                    // After the whole run, from either direction: dropping onto a
                    // fold means "somewhere in here is fine", and the end of it is
                    // the one position that is unambiguous.
                    if (dragFrom != null) {
                      const last = item.firstIndex + item.count - 1;
                      move(dragFrom, dragFrom < item.firstIndex ? last : last + 1);
                    }
                    setDragFrom(null);
                    setDropAt(null);
                  }}
                />
              ) : (
                <SheetLine
                  key={item.row.id}
                  row={item.row}
                  position={
                    item.row.included
                      ? rows.filter((r, i) => r.included && i <= item.index).length
                      : null
                  }
                  dragging={dragFrom === item.index}
                  dropBefore={dropAt === item.index}
                  canPreview={!!combineId}
                  onDragStart={(e) => {
                    // Firefox refuses a drop when the drag carries no data.
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", item.row.id);
                    setDragFrom(item.index);
                  }}
                  onDragEnd={() => {
                    setDragFrom(null);
                    setDropAt(null);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    const box = e.currentTarget.getBoundingClientRect();
                    const after = e.clientY > box.top + box.height / 2;
                    setDropAt(item.index + (after ? 1 : 0));
                  }}
                  onDrop={() => {
                    if (dragFrom != null && dropAt != null) {
                      move(dragFrom, dropAt > dragFrom ? dropAt - 1 : dropAt);
                    }
                    setDragFrom(null);
                    setDropAt(null);
                  }}
                  onUp={() => move(item.index, Math.max(0, item.index - 1))}
                  onDown={() => move(item.index, Math.min(rows.length - 1, item.index + 1))}
                  onToggle={() => onRows(toggleIncluded(rows, item.row.id))}
                  onAccept={() => onRows(acceptPosition(rows, item.row.id))}
                  onUseApproved={() => onRows(toggleUseApproved(rows, item.row.id))}
                  onPreview={() => setPreview({ row: item.row, side: "new" })}
                />
              ),
            )}
          </ol>
        </CardBody>
      </Card>

      {unbuildable.length > 0 && (
        <div className="mt-6">
          <div className="label mb-2">Not in the combined set</div>
          <Card>
            <CardBody className="space-y-3 py-4">
              {unbuildable.map((n) => (
                <div key={n.id} className="flex items-start gap-3">
                  <span
                    aria-hidden
                    className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-warn"
                  />
                  <div>
                    <span className="font-mono text-[0.85rem] text-ink">
                      {n.sheetId || "—"}
                    </span>
                    <p className="text-[0.88rem] leading-relaxed text-ink-soft">{n.warning}</p>
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <Button intent="ghost" size="md" onClick={onBack} disabled={busy}>
          ← Back
        </Button>
        <div className="flex items-center gap-3">
          {summary.pages === 0 && (
            <span className="text-[0.84rem] text-ink-muted">Nothing to build</span>
          )}
          <Button
            intent="primary"
            size="md"
            onClick={build}
            disabled={busy || summary.pages === 0}
          >
            {busy ? "Building…" : `Build combined PDF (${summary.pages} pages)`}
          </Button>
        </div>
      </div>

      <Dialog
        open={!!preview}
        onClose={() => setPreview(null)}
        size="xl"
        title={
          preview
            ? `${rowLabel(preview.row)} — ${
                preview.side === "old" ? "the sheet it replaces" : "as it will be filed"
              }`
            : ""
        }
      >
        {preview && combineId && (
          <div className="flex min-h-0 flex-1 flex-col">
            {preview.row.role === "Replaced" && (
              <div className="flex gap-2 border-b border-rule px-6 py-3">
                {(
                  [
                    ["new", "New sheet"],
                    ["old", "The one it replaces"],
                  ] as const
                ).map(([side, label]) => (
                  <button
                    key={side}
                    type="button"
                    onClick={() => setPreview({ row: preview.row, side })}
                    className={cn(
                      "rounded-md px-3 py-1.5 text-[0.84rem] font-medium transition-colors",
                      preview.side === side
                        ? "bg-primary-soft text-primary-ink"
                        : "text-ink-soft hover:bg-surface-2 hover:text-ink",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <div className="min-h-0 flex-1 overflow-auto bg-surface-2 p-4">
              <img
                src={combinePageImageUrl(
                  combineId,
                  preview.side === "old" ? "approved" : sideOf(preview.row),
                  (preview.side === "old" ? preview.row.replacesPage : preview.row.page) ?? 1,
                )}
                alt=""
                className="mx-auto max-w-full rounded-md bg-white shadow-soft"
              />
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}

function sideOf(row: CombineRow): "approved" | "updated" {
  return row.source === "Approved" ? "approved" : "updated";
}

// ---------------------------------------------------------------------------

function SheetLine({
  row,
  position,
  dragging,
  dropBefore,
  canPreview,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onUp,
  onDown,
  onToggle,
  onAccept,
  onUseApproved,
  onPreview,
}: {
  row: CombineRow;
  position: number | null;
  dragging: boolean;
  dropBefore: boolean;
  canPreview: boolean;
  onDragStart: (e: React.DragEvent<HTMLLIElement>) => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent<HTMLLIElement>) => void;
  onDrop: () => void;
  onUp: () => void;
  onDown: () => void;
  onToggle: () => void;
  onAccept: () => void;
  onUseApproved: () => void;
  onPreview: () => void;
}) {
  const out = !row.included;
  const canRevert = canUseApproved(row);

  return (
    <li
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={cn(
        "group relative flex items-start gap-3 px-4 py-2.5 transition-colors",
        dragging && "opacity-40",
        dropBefore && "shadow-[inset_0_2px_0_0_hsl(var(--primary))]",
        out && "bg-surface-2/60",
      )}
    >
      <span
        aria-hidden
        title="Drag to move"
        className="mt-1 cursor-grab select-none text-[0.9rem] leading-none text-ink-faint transition-colors group-hover:text-ink-muted"
      >
        ⠿
      </span>

      <span
        className={cn(
          "mt-0.5 w-7 shrink-0 text-right font-mono text-[0.78rem]",
          out ? "text-ink-faint" : "text-ink-muted",
        )}
      >
        {position ?? "—"}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <span
            className={cn(
              "font-mono text-[0.9rem] text-ink",
              out && "text-ink-muted line-through",
            )}
          >
            {effectivePageIdOf(row) || rowLabel(row)}
          </span>

          <RoleBadge role={row.role} out={out} useApproved={row.useApproved} />

          {/* The version NOT going in. Reading "was A-101.00" on a replacement
              and "instead of A-101.01" on one sent back is the same sentence
              either way round: this row carries one drawing and declines the
              other, and the one it declines is the thing worth naming. */}
          {row.role === "Replaced" && row.replacesSheetId && (
            <span className="text-[0.82rem] text-ink-muted">
              {row.useApproved ? "instead of " : "was "}
              <span className="font-mono">
                {row.useApproved ? pageIdOf(row) : approvedPageIdOf(row)}
              </span>
            </span>
          )}

          <span className="text-[0.78rem] text-ink-faint">
            {row.useApproved ? (
              <>
                approved p.{row.replacesPage} · updated p.{row.page} not used
              </>
            ) : (
              <>
                {row.source === "Approved" ? "approved" : "updated"} p.{row.page}
                {row.role === "Replaced" && row.replacesPage
                  ? ` · was approved p.${row.replacesPage}`
                  : ""}
              </>
            )}
          </span>

          {row.checkPosition && (
            <button
              type="button"
              onClick={onAccept}
              title="We worked this position out — click when you have checked it"
              className="rounded-full border border-butter/50 bg-butter-soft px-2 py-0.5 text-[0.72rem] font-medium text-butter-ink transition-colors hover:border-butter"
            >
              check position ✓
            </button>
          )}
        </div>

        {(row.placementReason || row.warning) && (
          <div className="mt-1 space-y-0.5">
            {row.warning && (
              <p className="text-[0.82rem] leading-snug text-ink">{row.warning}</p>
            )}
            {row.placementReason && (
              <p className="text-[0.82rem] leading-snug text-ink-muted">
                {row.placementReason}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Actions. Visible on hover/focus so a 60-row list is not a wall of
          buttons, but always reachable by keyboard. */}
      <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
        {canPreview && row.page != null && (
          <IconButton label="Look at this sheet" onClick={onPreview}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6Z" />
              <circle cx="12" cy="12" r="2.6" />
            </svg>
          </IconButton>
        )}
        <IconButton label="Move up" onClick={onUp}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 14l6-6 6 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </IconButton>
        <IconButton label="Move down" onClick={onDown}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 10l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </IconButton>
        {canRevert && !out && (
          <button
            type="button"
            onClick={onUseApproved}
            title={
              row.useApproved
                ? "Put the architect's new version back in"
                : "Keep the approved drawing and leave the new version out"
            }
            className="ml-1 rounded-md px-2 py-1 text-[0.78rem] font-medium text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink"
          >
            {row.useApproved ? "Use updated" : "Use approved"}
          </button>
        )}
        <button
          type="button"
          onClick={onToggle}
          className="ml-1 rounded-md px-2 py-1 text-[0.78rem] font-medium text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink"
        >
          {out ? "Keep it" : "Leave out"}
        </button>
      </div>
    </li>
  );
}

function RunLine({
  item,
  dropAt,
  onExpand,
  onDragOver,
  onDrop,
}: {
  item: Extract<CombineListItem, { kind: "run" }>;
  dropAt: number | null;
  onExpand: () => void;
  onDragOver: (e: React.DragEvent<HTMLLIElement>) => void;
  onDrop: () => void;
}) {
  return (
    <li
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={cn(
        "flex items-center gap-3 bg-surface-2/40 px-4 py-2",
        dropAt === item.firstIndex + item.count &&
          "shadow-[inset_0_-2px_0_0_hsl(var(--primary))]",
      )}
    >
      <span aria-hidden className="w-3.5" />
      <span className="w-7 shrink-0 text-right font-mono text-[0.78rem] text-ink-faint">
        {item.firstIndex + 1}–{item.firstIndex + item.count}
      </span>
      <button
        type="button"
        onClick={onExpand}
        className="text-left text-[0.84rem] text-ink-muted transition-colors hover:text-ink"
      >
        {item.count} unchanged sheets{" "}
        <span className="font-mono text-[0.8rem]">
          {item.from} … {item.to}
        </span>{" "}
        <span className="text-ink-faint">— show</span>
      </button>
    </li>
  );
}

function RoleBadge({
  role,
  out,
  useApproved,
}: {
  role: CombineRow["role"];
  out: boolean;
  useApproved: boolean;
}) {
  // An omitted sheet reads as "removed" whether or not it is in the file — the
  // AI1 says the sheet is being omitted, and a row that stopped saying so
  // because somebody kept the page would hide the disagreement instead of
  // showing it. "kept in" is the second half of that sentence, not a replacement
  // for it.
  if (role === "Removed") {
    return <Badge tone="warn">{out ? "removed" : "omit · kept in"}</Badge>;
  }
  if (out) return <Badge tone="neutral">left out</Badge>;
  switch (role) {
    case "Replaced":
      // Same reasoning as the omit above: the architect DID issue a new version
      // of this sheet, and a row that stopped saying so because the operator
      // declined it would hide the decision rather than show it.
      return useApproved ? (
        <Badge tone="warn">replaced · kept approved</Badge>
      ) : (
        <Badge tone="sky">replaced</Badge>
      );
    case "Added":
      return <Badge tone="sage">new</Badge>;
    default:
      return null;
  }
}

function Badge({
  tone,
  children,
}: {
  tone: "sky" | "sage" | "warn" | "neutral";
  children: React.ReactNode;
}) {
  const tones = {
    sky: "bg-sky-soft text-sky-ink",
    sage: "bg-sage-soft text-sage-ink",
    warn: "bg-warn-soft text-ink",
    neutral: "bg-surface-2 text-ink-muted",
  };
  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-[0.72rem] font-medium uppercase tracking-wide",
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

function Chip({
  tone,
  count,
  label,
}: {
  tone: "sky" | "sage" | "warn" | "butter" | "neutral";
  count: number;
  label: string;
}) {
  const dots = {
    sky: "bg-sky",
    sage: "bg-sage",
    warn: "bg-warn",
    butter: "bg-butter",
    neutral: "bg-rule-strong",
  };
  if (count === 0) return null;
  return (
    <div className="flex items-center gap-2">
      <span aria-hidden className={cn("h-2 w-2 rounded-full", dots[tone])} />
      <span className="text-[0.9rem] text-ink">{count}</span>
      <span className="text-[0.84rem] text-ink-muted">{label}</span>
    </div>
  );
}

function IconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="flex h-7 w-7 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink"
    >
      <span className="h-4 w-4 [&>svg]:h-full [&>svg]:w-full">{children}</span>
    </button>
  );
}

function Notice({
  tone,
  title,
  children,
}: {
  tone: "warn" | "sky";
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "mb-4 rounded-lg border px-4 py-3",
        tone === "warn" ? "border-warn/30 bg-warn-soft" : "border-sky/30 bg-sky-soft",
      )}
    >
      <div className="text-[0.88rem] font-semibold text-ink">{title}</div>
      <p className="mt-1 max-w-2xl text-[0.88rem] leading-relaxed text-ink-soft">
        {children}
      </p>
    </div>
  );
}

// Exported for the tests: the position label is what the telemetry records, and
// it has to mean the same thing on screen as it does in the record.
export { positionLabel };
